import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import InventoryLayout from './InventoryLayout';

const OrderReceive = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null;
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const loadOrder = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/orders/${id}`, { headers });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || 'Kunne ikke hente ordre');
        setOrder(data.order);
        setLines(
          (data.lines || []).map((line) => ({
            ...line,
            receive_now: Math.max(0, line.qty - line.received_qty)
          }))
        );
      } catch (err) {
        setError(err.message || 'Kunne ikke hente ordre');
      } finally {
        setLoading(false);
      }
    };
    loadOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const updateReceive = (lineId, value) => {
    setLines((prev) =>
      prev.map((line) =>
        line.id === lineId ? { ...line, receive_now: Number(value) } : line
      )
    );
  };

  const handleSubmit = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        lines: lines
          .filter((line) => line.receive_now > 0)
          .map((line) => ({ line_id: line.id, received_qty: line.receive_now }))
      };
      const res = await fetch(`/api/orders/${id}/receive`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || 'Kunne ikke gemme modtagelse');
      navigate(`/indkoeb/ordrer/${id}`);
    } catch (err) {
      setError(err.message || 'Kunne ikke gemme modtagelse');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <InventoryLayout>
        <div className="p-4 text-gray-500">Indlæser...</div>
      </InventoryLayout>
    );
  }

  if (!order) {
    return (
      <InventoryLayout>
        <div className="p-4">
          <p className="text-gray-500">Ordre ikke fundet.</p>
          <button onClick={() => navigate('/indkoeb/ordrer')} className="text-falck-blue underline">
            Tilbage
          </button>
        </div>
      </InventoryLayout>
    );
  }

  return (
    <InventoryLayout>
      <h2 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-4">
        Modtag ordre #{order.id}
      </h2>
      {error && <div className="mb-3 p-3 bg-red-100 text-red-700 rounded">{error}</div>}

      <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow p-4 space-y-3">
        {lines.map((line) => (
          <div key={line.id} className="grid grid-cols-1 md:grid-cols-4 gap-2 items-center">
            <div>
              <div className="font-medium">{line.name}</div>
              <div className="text-xs text-gray-500">Varenummer: {line.sku || '—'}</div>
            </div>
            <div className="text-sm text-gray-600">Bestilt: {line.qty}</div>
            <div className="text-sm text-gray-600">Modtaget: {line.received_qty}</div>
            <input
              type="number"
              min="0"
              max={line.qty - line.received_qty}
              value={line.receive_now}
              onChange={(e) => updateReceive(line.id, e.target.value)}
              className="border border-gray-300 dark:border-gray-700 rounded-md px-3 py-2"
            />
          </div>
        ))}
        <div className="flex justify-end gap-2 pt-4">
          <button onClick={() => navigate(`/indkoeb/ordrer/${id}`)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md">
            Annuller
          </button>
          <button onClick={handleSubmit} disabled={saving} className="px-4 py-2 bg-falck-blue text-white rounded-md disabled:opacity-60">
            {saving ? 'Gemmer...' : 'Gem modtagelse'}
          </button>
        </div>
      </div>
    </InventoryLayout>
  );
};

export default OrderReceive;
